"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import {
  findNumberSpans,
  parseWholeNumberWords,
} from "@/lib/tool-kit/number-words/parse";

const RE_HYPHEN = /-/g;
const RE_SPLIT = /\s+/;
const RE_WORD_TOKEN = /[A-Za-z]+(?:-[A-Za-z]+)*/g;

const DIGIT_WORDS: Record<string, string> = {
  zero: "0",
  one: "1",
  two: "2",
  three: "3",
  four: "4",
  five: "5",
  six: "6",
  seven: "7",
  eight: "8",
  nine: "9",
};

function tokenize(input: string): string[] {
  return input
    .toLowerCase()
    .replace(RE_HYPHEN, " ")
    .trim()
    .split(RE_SPLIT)
    .filter(Boolean);
}

function parseDecimalTokens(tokens: string[]): string | null {
  if (tokens.length === 0) {
    return null;
  }
  const digits: string[] = [];
  for (const token of tokens) {
    const digit = DIGIT_WORDS[token];
    if (digit === undefined) {
      return null;
    }
    digits.push(digit);
  }
  return digits.join("");
}

export function wordsToNumber(input: string): string | null {
  let tokens = tokenize(input);
  if (tokens.length === 0) {
    return null;
  }
  let sign = "";
  if (tokens[0] === "negative" || tokens[0] === "minus") {
    sign = "-";
    tokens = tokens.slice(1);
  }
  const pointIndex = tokens.indexOf("point");
  const integerTokens =
    pointIndex === -1 ? tokens : tokens.slice(0, pointIndex);
  const integer = parseWholeNumberWords(integerTokens);
  if (integer === null) {
    return null;
  }
  if (pointIndex === -1) {
    return `${sign}${integer}`;
  }
  const decimal = parseDecimalTokens(tokens.slice(pointIndex + 1));
  if (decimal === null) {
    return null;
  }
  return `${sign}${integer}.${decimal}`;
}

const RE_WHITESPACE_ONLY = /^\s*$/;

export function replaceNumbersInText(text: string): string {
  const matches = Array.from(text.matchAll(RE_WORD_TOKEN));
  const words = matches.map((match) => match[0].toLowerCase().split("-"));
  // A number word joins the one before it only across white space, never across
  // punctuation or another word.
  const adjacent = matches.map((match, index) => {
    const previous = matches[index - 1];
    if (previous === undefined) {
      return true;
    }
    const gap = text.slice(previous.index + previous[0].length, match.index);
    return RE_WHITESPACE_ONLY.test(gap);
  });
  let result = "";
  let cursor = 0;
  for (const span of findNumberSpans(words, adjacent)) {
    const first = matches[span.from];
    const last = matches[span.to];
    result += text.slice(cursor, first.index) + span.value;
    cursor = last.index + last[0].length;
  }
  return result + text.slice(cursor);
}

type Mode = "single" | "text";

/** English strings. A translated content file overrides these through `ui`. */
export const WORD_TO_NUMBER_STRINGS = {
  mode: "Mode",
  singleNumber: "Single number",
  wholeText: "Whole text (replace every number)",
  writtenNumber: "Written number",
  singleInputPlaceholder: "two hundred forty-six",
  asDigits: "As digits",
  copied: "Copied!",
  copy: "Copy",
  parseError:
    'Could not parse that as a number. Use words like "four hundred twelve", "forty-two", "negative seven", or "three point one four" — hyphens and "and" are fine.',
  textContaining: "Text containing written numbers",
  textInputPlaceholder:
    "The survey had three hundred twelve responses over twenty-one days.",
  convert: "Convert",
  copyResult: "Copy Result",
  resultLabel: "Result — written numbers replaced with digits",
} as const;

export default function WordToNumberWidget() {
  const strings = useToolUi(WORD_TO_NUMBER_STRINGS);
  const [mode, setMode] = useState<Mode>("single");
  const [input, setInput] = useState("");
  const [textInput, setTextInput] = useState("");
  const [textOutput, setTextOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const singleResult = input.trim().length > 0 ? wordsToNumber(input) : null;
  const showSingleError = input.trim().length > 0 && singleResult === null;

  function handleConvertText() {
    if (textInput.trim().length === 0) {
      return;
    }
    setTextOutput(replaceNumbersInText(textInput));
    setCopied(false);
  }

  function handleCopy(value: string) {
    writeToClipboard(value).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.mode}</p>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "single"}
              className="accent-primary"
              name="wtn-mode"
              onChange={() => setMode("single")}
              type="radio"
              value="single"
            />
            {strings.singleNumber}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "text"}
              className="accent-primary"
              name="wtn-mode"
              onChange={() => setMode("text")}
              type="radio"
              value="text"
            />
            {strings.wholeText}
          </label>
        </div>
      </section>

      {mode === "single" && (
        <section className="space-y-3">
          <div className="space-y-2">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="wtn-input"
            >
              {strings.writtenNumber}
            </label>
            <input
              className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-lg leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              id="wtn-input"
              onChange={(e) => setInput(e.target.value)}
              placeholder={strings.singleInputPlaceholder}
              type="text"
              value={input}
            />
          </div>
          {singleResult !== null && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 p-4">
              <div>
                <p className="text-muted-foreground text-xs">
                  {strings.asDigits}
                </p>
                <p className="font-mono font-semibold text-2xl text-foreground">
                  {singleResult}
                </p>
              </div>
              <button
                className="rounded-lg border border-border bg-card px-4 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted"
                onClick={() => handleCopy(singleResult)}
                type="button"
              >
                {copied ? strings.copied : strings.copy}
              </button>
            </div>
          )}
          {showSingleError && (
            <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
              {strings.parseError}
            </p>
          )}
        </section>
      )}

      {mode === "text" && (
        <section className="space-y-3">
          <div className="space-y-2">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="wtn-text-input"
            >
              {strings.textContaining}
            </label>
            <textarea
              className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              id="wtn-text-input"
              onChange={(e) => setTextInput(e.target.value)}
              placeholder={strings.textInputPlaceholder}
              value={textInput}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
              disabled={textInput.trim().length === 0}
              onClick={handleConvertText}
              type="button"
            >
              {strings.convert}
            </button>
            <button
              className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
              disabled={textOutput.length === 0}
              onClick={() => handleCopy(textOutput)}
              type="button"
            >
              {copied ? strings.copied : strings.copyResult}
            </button>
          </div>
          {textOutput.length > 0 && (
            <div className="space-y-2">
              <label
                className="font-medium text-foreground text-sm"
                htmlFor="wtn-text-output"
              >
                {strings.resultLabel}
              </label>
              <textarea
                className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
                id="wtn-text-output"
                readOnly
                value={textOutput}
              />
            </div>
          )}
        </section>
      )}
    </>
  );
}
